/**
 * User-defined document workspaces (owner decision 13.10.2026, card 4.0 option
 * A; card 5.1) — the `docscope` records. UI word "Workspace"; code word
 * `docscope` / `scopeId`, because `workspaceId` already names the records
 * bucket.
 *
 * A docscope is `{ id, name, icon, color, position }` in the PERSONAL bucket of
 * extension_documents, synced like folders. Documents point at it from their
 * root row (`scopeId`, see documentsScopeTree.js). The device scope and team
 * scopes have no sub-workspaces: every function here works on the personal
 * bucket only.
 *
 * Deleting a workspace never deletes a document — its roots move to Personal
 * (or `moveTo`) first, then the docscope row goes. A crash in between leaves a
 * workspace that is empty, never documents that are gone; and a root still
 * naming a deleted workspace shows under Personal (`listTree`'s known-ids
 * check), never nowhere.
 */
import { deleteRecord, listRecords, upsertRecord } from '../../../records/recordsClient';
import { PROJECT_COLORS } from '../../../graph/projectColors';
import { recordsContext, uid } from './documentsStore';
import { clearedScope, dataOf } from './documentsScopeTree';

const EXTENSION_ID = 'documents';
const RECORD_TYPE = 'docscope';
const PERSONAL = { type: 'personal' };
export const SCOPE_NAME_MAX = 60;

/**
 * The eight brand colours as hex — the app's project colours, which is exactly
 * what the backend accepts (`Sync.DocScope` @colors, PR #213).
 */
export const SCOPE_COLORS = PROJECT_COLORS;
export const DEFAULT_SCOPE_COLOR = PROJECT_COLORS[0];

// The first build of this module (PR #600, never synced: the backend was not
// deployed) stored board-label colour KEYS. Read them as the nearest hex.
const LEGACY_COLOR_KEYS = {
  purple: '#8869eb',
  blue: '#3b82f6',
  green: '#10b981',
  amber: '#f59e0b',
  orange: '#f59e0b',
  red: '#ef4444',
  pink: '#ec4899',
  gray: '#64748b',
};

/**
 * Icon keys a workspace may carry — the backend's list (`Sync.DocScope`
 * @icons) one for one; the picker maps them to glyphs (workspaceIcons.js). A
 * new key needs a backend deploy first, or the server refuses the record.
 */
export const SCOPE_ICONS = Object.freeze([
  'folder', 'briefcase', 'home', 'book', 'bookmark', 'star', 'heart', 'code', 'terminal', 'rocket',
  'flask', 'globe', 'lightbulb', 'layers', 'user', 'users', 'archive', 'calendar', 'compass', 'flag',
  'pen', 'leaf', 'coffee', 'music', 'camera', 'graduation', 'target', 'box',
]);
export const DEFAULT_SCOPE_ICON = 'folder';

const ctxOf = () => recordsContext(PERSONAL);

/** Errors carry a stable `code` the UI translates (the message is for logs). */
function scopeError(code, message) {
  const err = new Error(message);
  err.code = code;
  return err;
}

function cleanName(name) {
  const trimmed = String(name ?? '').trim();
  if (!trimmed) throw scopeError('scope_name_required', 'A workspace needs a name.');
  return trimmed.slice(0, SCOPE_NAME_MAX);
}

const cleanIcon = (icon) => (SCOPE_ICONS.includes(icon) ? icon : DEFAULT_SCOPE_ICON);
function cleanColor(color) {
  const value = typeof color === 'string' ? color.toLowerCase() : color;
  if (SCOPE_COLORS.includes(value)) return value;
  return LEGACY_COLOR_KEYS[value] || DEFAULT_SCOPE_COLOR;
}

function toScope(row) {
  return {
    id: String(row.id),
    name: row.name || '',
    icon: cleanIcon(row.icon),
    color: cleanColor(row.color),
    position: Number.isFinite(Number(row.position)) ? Number(row.position) : 0,
  };
}

const byPosition = (a, b) => a.position - b.position || a.name.localeCompare(b.name) || a.id.localeCompare(b.id);

async function listRows(ctx, recordType) {
  return (
    (await listRecords({ workspaceId: ctx.workspaceId, extensionId: EXTENSION_ID, recordType, scopeId: null })) || []
  );
}

async function writeScope(ctx, scope) {
  await upsertRecord({
    workspaceId: ctx.workspaceId,
    extensionId: EXTENSION_ID,
    record: {
      id: scope.id,
      record_type: RECORD_TYPE,
      parent_id: null,
      // Ordering lives in `position` (the picker's drag order); sort_key only
      // keeps the Rust listing stable.
      sort_key: String(scope.position).padStart(15, '0'),
      data: { name: scope.name, icon: scope.icon, color: scope.color, position: scope.position },
    },
  });
}

/**
 * The user's workspaces in picker order. `scope` is the descriptor the caller
 * is looking at: anything but personal has none (device/team), so `[]`.
 */
export async function listScopes(scope = PERSONAL) {
  if (scope?.type && scope.type !== 'personal') return [];
  return (await listRows(ctxOf(), RECORD_TYPE)).map(toScope).sort(byPosition);
}

/** Creates a workspace at the end of the list and returns it. */
export async function createScope({ name, icon, color } = {}) {
  const existing = await listScopes();
  const scope = {
    id: uid(),
    name: cleanName(name),
    icon: cleanIcon(icon),
    color: cleanColor(color),
    position: existing.reduce((max, s) => Math.max(max, s.position), -1) + 1,
  };
  await writeScope(ctxOf(), scope);
  return scope;
}

/**
 * A workspace with exactly this id, made if it is missing (git sync import,
 * card 4.5: a repository names workspaces by id). An existing one is left as
 * it is — its name in the app wins. Resolves the workspace.
 */
export async function ensureScope({ id, name }) {
  const existing = await listScopes();
  const found = existing.find((s) => s.id === String(id));
  if (found) return found;
  const scope = {
    id: String(id),
    name: cleanName(name),
    icon: DEFAULT_SCOPE_ICON,
    color: DEFAULT_SCOPE_COLOR,
    position: existing.reduce((max, s) => Math.max(max, s.position), -1) + 1,
  };
  await writeScope(ctxOf(), scope);
  return scope;
}

async function findScope(id) {
  const found = (await listScopes()).find((s) => s.id === String(id));
  if (!found) throw scopeError('scope_not_found', `Workspace "${id}" does not exist.`);
  return found;
}

/** Renames (and optionally re-icons / re-colours) a workspace. */
export async function renameScope(id, name, { icon, color } = {}) {
  const current = await findScope(id);
  const next = {
    ...current,
    name: name == null ? current.name : cleanName(name),
    icon: icon === undefined ? current.icon : cleanIcon(icon),
    color: color === undefined ? current.color : cleanColor(color),
  };
  await writeScope(ctxOf(), next);
  return next;
}

/** Writes `position` 0..n-1 in the given order; ids not listed keep their place after them. */
export async function reorderScopes(orderedIds) {
  const all = await listScopes();
  const wanted = (orderedIds || []).map(String);
  const ordered = [
    ...wanted.map((id) => all.find((s) => s.id === id)).filter(Boolean),
    ...all.filter((s) => !wanted.includes(s.id)),
  ];
  const ctx = ctxOf();
  const out = [];
  for (let i = 0; i < ordered.length; i += 1) {
    const next = { ...ordered[i], position: i };
    // eslint-disable-next-line no-await-in-loop
    if (next.position !== ordered[i].position) await writeScope(ctx, next);
    out.push(next);
  }
  return out;
}

/**
 * Deletes a workspace. Its documents are NOT deleted: every root naming it is
 * rewritten to `moveTo` (another workspace id) or to Personal (`null`, the
 * default — written as `scopeId: null`), keeping every other data key, BEFORE
 * the docscope row goes.
 * Resolves `{ moved }` — how many roots changed workspace.
 */
export async function deleteScope(id, { moveTo = null } = {}) {
  const target = moveTo == null ? null : String(moveTo);
  if (target === String(id)) throw scopeError('scope_move_into_self', 'A workspace cannot move its documents into itself.');
  await findScope(id);
  if (target) await findScope(target);
  const ctx = ctxOf();
  const [folders, documents] = await Promise.all([listRows(ctx, 'folder'), listRows(ctx, 'document')]);
  let moved = 0;
  for (const row of [...folders, ...documents]) {
    if (String(row.scopeId || '') !== String(id)) continue;
    // To Personal = an explicit `scopeId: null` (clearedScope): a missing key
    // would leave the old workspace on the server.
    const data = target ? { ...dataOf(row), scopeId: target } : clearedScope(dataOf(row));
    // eslint-disable-next-line no-await-in-loop
    await upsertRecord({
      workspaceId: ctx.workspaceId,
      extensionId: EXTENSION_ID,
      record: { id: row.id, record_type: row.record_type, parent_id: row.parent_id ?? null, sort_key: row.sort_key, data },
    });
    moved += 1;
  }
  await deleteRecord({ workspaceId: ctx.workspaceId, extensionId: EXTENSION_ID, recordId: String(id) });
  return { moved };
}
